// app/register/page.jsx
"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import Link from "next/link";
import { useToast } from "@/hooks/use-toast";

// Import form components
import { ExpertRegistrationForm } from "@/components/forms/expertRegistrationForm";
import { NormalUserRegistrationForm } from "@/components/forms/NormalUserRegistrationForm";
import { OrganizationRegistrationForm } from "@/components/forms/OrganizationRegistrationForm";
import { formConfig } from "@/lib/formConfig";

export default function RegisterPage({ 
  education, 
  educationMaj, 
  Gender, 
  Counties, 
  Cities, 
  ExpField, 
  WorkExp, 
  WorkSkills, 
  Proficiency, 
  Currincies, 
  Travel 
}) {
  const [userType, setUserType] = useState("normal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const router = useRouter();

  // Handle form submission based on user type
 const handleSubmit = async (formData) => {
  setIsSubmitting(true);

  try {
    const payload = { ...formData, userType };
    const response = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (response.ok) {
      toast({
        title: "Registration Successful!",
        description: "Please check your email to verify your account.",
      });
      router.push("/verify-email");
    } else {
      throw new Error(data.error || data.message || "Registration failed");
    }
  } catch (error) {
    
    toast({
      title: "Registration Failed",
      description: error.message,
      variant: "destructive",
    });
  } finally {
    setIsSubmitting(false);
  }
};

  const renderForm = () => {
    const commonProps = {
      onSubmit: handleSubmit,
      isSubmitting,
    };

    switch (userType) {
      case "expert":
        return (
          <ExpertRegistrationForm
            {...commonProps}
            education={education}
            educationMaj={educationMaj}
            Gender={Gender}
            Counties={Counties}
            Cities={Cities}
            ExpField={ExpField}
            WorkExp={WorkExp}
            WorkSkills={WorkSkills}
            Proficiency={Proficiency}
            Currincies={Currincies}
            Travel={Travel}
          />
        );
      case "employee":
      case "ngo":
        return <OrganizationRegistrationForm {...commonProps} userType={userType} />;
      default:
        return <NormalUserRegistrationForm {...commonProps} />;
    }
  };

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <Card className="shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-bold">Create Account</CardTitle>
          <CardDescription>
            Join our humanitarian platform to make a difference
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <Label htmlFor="userType" className="text-base font-medium">
              I am registering as:
            </Label>
            <Select value={userType} onValueChange={setUserType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select account type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Account Type</SelectLabel>
                  {formConfig.userTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {renderForm()}
        </CardContent>

        <CardFooter className="flex flex-col space-y-4 border-t pt-6">
          <p className="text-sm text-muted-foreground text-center">
            Already have an account?{" "}
            <Link href="/login" className="text-primary hover:underline font-medium">
              Sign in here
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}